// Project — Sprint 12 orchestrator. Now a thin layout shell:
//  * data + persistence  → useProjectsData
//  * pure helpers/tags   → projectRules
//  * presentation        → ProjectsStatsRow + ProjectsFilterBar +
//                          ProjectsTable
//  * 3 modals            → ProjectFormModal (Add+Edit unified) +
//                          ProjectViewModal
// The orchestrator only owns transient UI state (which modal is
// open, which row is selected, current filter values, AntD Form
// instances).
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Form } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { staticApi } from '../utils/staticApi';
import { useAuth } from '../../../context/UnifiedAuthContext';
import { useAuthStore } from '../store/authStore';
import { useProjectsData } from './projects/useProjectsData';
import { ProjectsStatsRow } from './projects/ProjectsStatsRow';
import { ProjectsFilterBar } from './projects/ProjectsFilterBar';
import { ProjectsTable } from './projects/ProjectsTable';
import { ProjectFormModal } from './projects/ProjectFormModal';
import { ProjectViewModal } from './projects/ProjectViewModal';
import {
  filterProjects, getAvailableYears, summarizeProjects,
} from './projects/projectRules';
import type {
  Project, ProjectSearchFilters,
} from './projects/projectTypes';

// Re-export the types so existing import sites (e.g.
// `import type { Project } from '.../Project'`) keep compiling.
export type { Project, ProjectSummary } from './projects/projectTypes';

export const Projects: React.FC = () => {
  const { projects, fetchProjects, createProject, updateProject, confirmDelete } = useProjectsData();
  const { user } = useAuth();
  const { currentCondo } = useAuthStore();
  const condoId = currentCondo?.id;

  const canManageProjects = user?.role === 'super_admin_condo' || user?.role === 'admin';

  // Load this condo's projects on mount and whenever the active condo changes.
  useEffect(() => {
    if (!condoId) return;
    fetchProjects(condoId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- condo-scoped fetch-on-mount; fetchProjects is a stable zustand action
  }, [condoId]);

  // ── Transient UI state ──────────────────────────────────────────────
  const [filters,         setFilters]         = useState<ProjectSearchFilters>({});
  const [addOpen,         setAddOpen]         = useState(false);
  const [editOpen,        setEditOpen]        = useState(false);
  const [viewOpen,        setViewOpen]        = useState(false);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [addForm]  = Form.useForm();
  const [editForm] = Form.useForm();

  // ── Derived data ────────────────────────────────────────────────────
  const availableYears = useMemo(() => getAvailableYears(projects), [projects]);
  const filtered       = useMemo(() => filterProjects(projects, filters), [projects, filters]);
  const summary        = useMemo(() => summarizeProjects(filtered),     [filtered]);

  // ── Modal openers ───────────────────────────────────────────────────
  const openAdd = () => {
    addForm.resetFields();
    setAddOpen(true);
  };

  const openEdit = (project: Project) => {
    setSelectedProject(project);
    editForm.setFieldsValue({
      name:                project.name,
      description:         project.description,
      budget:              project.budget,
      actualCost:          project.actualCost,
      responsiblePerson:   project.responsiblePerson,
      responsiblePersonId: project.responsiblePersonId,
      dateRange:           [dayjs(project.startDate), dayjs(project.endDate)],
      status:              project.status,
      priority:            project.priority,
      category:            project.category,
      notes:               project.notes,
    });
    setEditOpen(true);
  };

  const openView = (project: Project) => {
    setSelectedProject(project);
    setViewOpen(true);
  };

  // ── Submit handlers ─────────────────────────────────────────────────
  // Server owns the id + timestamps; we send the real active condoId (the
  // old 'default-condo' placeholder never matched a tenant). ADR-147.
  const handleAddSubmit = async (values: any) => {
    if (!condoId) {
      staticApi.message.error('No active condo selected');
      return;
    }
    try {
      await createProject({
        condoId,
        name:                values.name,
        description:         values.description,
        budget:              values.budget,
        actualCost:          values.actualCost || 0,
        responsiblePerson:   values.responsiblePerson,
        responsiblePersonId: values.responsiblePersonId,
        startDate:           new Date(values.dateRange[0]),
        endDate:             new Date(values.dateRange[1]),
        status:              values.status   || 'PLANNING',
        priority:            values.priority || 'MEDIUM',
        category:            values.category,
        notes:               values.notes,
      });
      staticApi.message.success('Project added successfully');
      setAddOpen(false);
      addForm.resetFields();
    } catch {
      staticApi.message.error('Failed to add project');
    }
  };

  const handleEditSubmit = async (values: any) => {
    if (!selectedProject) return;
    try {
      await updateProject(selectedProject.id, {
        ...selectedProject,
        name:                values.name,
        description:         values.description,
        budget:              values.budget,
        actualCost:          values.actualCost,
        responsiblePerson:   values.responsiblePerson,
        responsiblePersonId: values.responsiblePersonId,
        startDate:           new Date(values.dateRange[0]),
        endDate:             new Date(values.dateRange[1]),
        status:              values.status,
        priority:            values.priority,
        category:            values.category,
        notes:               values.notes,
      });
      staticApi.message.success('Project updated successfully');
      setEditOpen(false);
      setSelectedProject(null);
      editForm.resetFields();
    } catch {
      staticApi.message.error('Failed to update project');
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Project Management</h1>
          <p className="text-gray-600">
            {canManageProjects
              ? 'Manage condo projects, budgets, and track progress'
              : 'View project details and progress'}
          </p>
        </div>
        {canManageProjects && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
            Add New Project
          </Button>
        )}
      </div>

      <ProjectsStatsRow summary={summary} />

      <ProjectsFilterBar
        filters={filters}
        availableYears={availableYears}
        onChange={setFilters}
        onClear={() => setFilters({})}
      />

      <ProjectsTable
        rows={filtered}
        canManage={canManageProjects}
        onView={openView}
        onEdit={openEdit}
        onDelete={(id) => condoId && confirmDelete(id, condoId)}
      />

      <ProjectFormModal
        open={addOpen}
        mode="add"
        form={addForm}
        onCancel={() => { setAddOpen(false); addForm.resetFields(); }}
        onSubmit={handleAddSubmit}
      />

      <ProjectFormModal
        open={editOpen}
        mode="edit"
        form={editForm}
        onCancel={() => {
          setEditOpen(false);
          setSelectedProject(null);
          editForm.resetFields();
        }}
        onSubmit={handleEditSubmit}
      />

      <ProjectViewModal
        open={viewOpen}
        project={selectedProject}
        canManage={canManageProjects}
        onClose={() => { setViewOpen(false); setSelectedProject(null); }}
        onEdit={(p) => { setViewOpen(false); openEdit(p); }}
      />
    </div>
  );
};
